import React, { useEffect, useState, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import CarCard from '../components/CarCard'
import GoogleMap from '../components/GoogleMap'

const API_URL = '/_fn/cars'

export default function Cars() {
  const [cars, setCars] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedMake, setSelectedMake] = useState('all')
  const [selectedFuel, setSelectedFuel] = useState('all')
  const [maxPrice, setMaxPrice] = useState('all')
  const [minYear, setMinYear] = useState('all')
  const [maxKm, setMaxKm] = useState('all')
  const [selectedTransmission, setSelectedTransmission] = useState('all')
  const [sortBy, setSortBy] = useState('newest') // 'newest', 'price-asc', 'price-desc', 'km-asc'

  // UI State
  const [makeSearch, setMakeSearch] = useState('')
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)
  const [visibleCount, setVisibleCount] = useState(12)
  const sentinelRef = useRef(null)
  const sidebarContainerRef = useRef(null)
  const sidebarRef = useRef(null)

  // Fetch cars on mount
  useEffect(() => {
    setLoading(true)
    setError(null)

    fetch(API_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`Fejl ved hentning af biler (${res.status})`)
        return res.json()
      })
      .then((data) => {
        const list = data.vehicles || (Array.isArray(data) ? data : [])
        setCars(list)
        setLoading(false)
      })
      .catch((err) => {
        console.error('Fejl ved hentning af Bilinfo biler:', err)
        setError('Der opstod en fejl under indlæsning af biler. Prøv venligst igen.')
        setLoading(false)
      })
  }, [])

  // Lock body scroll when mobile filter is open
  useEffect(() => {
    if (isMobileFilterOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileFilterOpen])

  // Aggregate makes with counts
  const makesWithCount = useMemo(() => {
    const map = new Map()
    cars.forEach((c) => {
      const m = c.Make || c.maerke?.da || c.maerke
      if (m) {
        map.set(m, (map.get(m) || 0) + 1)
      }
    })
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name, 'da'))
  }, [cars])

  // Filtered makes in brand list (with mini search)
  const filteredMakesList = useMemo(() => {
    if (!makeSearch.trim()) return makesWithCount
    const q = makeSearch.toLowerCase()
    return makesWithCount.filter((item) => item.name.toLowerCase().includes(q))
  }, [makesWithCount, makeSearch])

  // Aggregate fuel types with counts
  const fuelsWithCount = useMemo(() => {
    const counts = {
      all: cars.length,
      el: 0,
      benzin: 0,
      diesel: 0,
      hybrid: 0,
    }

    cars.forEach((c) => {
      const f = (c.fuel || c.Propellant || c.braendstof?.da || c.braendstof || '').toLowerCase()
      if (f.includes('el') && !f.includes('diesel')) {
        counts.el += 1
      } else if (f.includes('diesel')) {
        counts.diesel += 1
      } else if (f.includes('benzin')) {
        counts.benzin += 1
      } else if (f.includes('hybrid') || f.includes('phev')) {
        counts.hybrid += 1
      }
    })

    return counts
  }, [cars])

  // Count active filters (excluding defaults and sort)
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (searchTerm.trim()) count += 1
    if (selectedMake !== 'all') count += 1
    if (selectedFuel !== 'all') count += 1
    if (maxPrice !== 'all') count += 1
    if (minYear !== 'all') count += 1
    if (maxKm !== 'all') count += 1
    if (selectedTransmission !== 'all') count += 1
    return count
  }, [searchTerm, selectedMake, selectedFuel, maxPrice, minYear, maxKm, selectedTransmission])

  // Filter and sort vehicles
  const filteredCars = useMemo(() => {
    let list = [...cars]

    // 1. Search term: matches make, model, variant, comment and equipment
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      list = list.filter((car) => {
        const title = (car.title || `${car.Make || ''} ${car.Model || ''} ${car.Variant || ''}` || '').toLowerCase()
        const comment = (car.Comment || '').toLowerCase()
        let equipmentStr = ''
        if (Array.isArray(car.Equipment)) {
          equipmentStr = car.Equipment.join(' ').toLowerCase()
        } else if (typeof car.Equipment === 'string') {
          equipmentStr = car.Equipment.toLowerCase()
        }
        return title.includes(term) || comment.includes(term) || equipmentStr.includes(term)
      })
    }

    // 2. Make filter
    if (selectedMake !== 'all') {
      list = list.filter((car) => (car.Make || car.maerke?.da || car.maerke) === selectedMake)
    }

    // 3. Fuel filter
    if (selectedFuel !== 'all') {
      list = list.filter((car) => {
        const fuel = (car.fuel || car.Propellant || car.braendstof?.da || car.braendstof || '').toLowerCase()
        if (selectedFuel === 'el') return fuel.includes('el') && !fuel.includes('diesel')
        if (selectedFuel === 'benzin') return fuel.includes('benzin')
        if (selectedFuel === 'diesel') return fuel.includes('diesel')
        if (selectedFuel === 'hybrid') return fuel.includes('hybrid') || fuel.includes('phev')
        return fuel === selectedFuel.toLowerCase()
      })
    }

    // 4. Max Price filter
    if (maxPrice !== 'all') {
      const maxP = parseInt(maxPrice, 10)
      list = list.filter((car) => {
        const price = parseInt(car.kontantpris || car.CashPrice || car.pris || car.price || car.Price || car.leasingpris || car.LeasingPrice || 0, 10)
        return price > 0 && price <= maxP
      })
    }

    // 5. Min Year filter
    if (minYear !== 'all') {
      const minY = parseInt(minYear, 10)
      list = list.filter((car) => {
        const year = car.year || parseInt(car.Year || car.aargang || 0, 10)
        return year >= minY
      })
    }

    // 6. Max Mileage (Km) filter
    if (maxKm !== 'all') {
      const maxKilometers = parseInt(maxKm, 10)
      list = list.filter((car) => {
        const mileage = car.mileage ?? parseInt(car.Mileage || car.kilometer || 0, 10)
        return mileage <= maxKilometers
      })
    }

    // 7. Transmission (Gearkasse) filter
    if (selectedTransmission !== 'all') {
      list = list.filter((car) => {
        const trans = (car.transmission || (car.GearType === 'A' ? 'Automatgear' : (car.GearType === 'M' ? 'Manuel' : car.GearType)) || '').toLowerCase()
        if (selectedTransmission === 'automat') {
          return trans.includes('auto') || trans === 'a'
        }
        if (selectedTransmission === 'manuel') {
          return trans.includes('manuel') || trans === 'm'
        }
        return true
      })
    }

    // 8. Sorting
    list.sort((a, b) => {
      const priceA = parseInt(a.kontantpris || a.CashPrice || a.pris || a.price || a.Price || a.leasingpris || a.LeasingPrice || 0, 10)
      const priceB = parseInt(b.kontantpris || b.CashPrice || b.pris || b.price || b.Price || b.leasingpris || b.LeasingPrice || 0, 10)
      const kmA = a.mileage ?? parseInt(a.Mileage || a.kilometer || 0, 10)
      const kmB = b.mileage ?? parseInt(b.Mileage || b.kilometer || 0, 10)
      const yearA = a.year || parseInt(a.Year || a.aargang || 0, 10)
      const yearB = b.year || parseInt(b.Year || b.aargang || 0, 10)

      if (sortBy === 'price-asc') return priceA - priceB
      if (sortBy === 'price-desc') return priceB - priceA
      if (sortBy === 'km-asc') return kmA - kmB
      // default 'newest': newest year first, then lower mileage
      if (yearB !== yearA) return yearB - yearA
      return kmA - kmB
    })

    return list
  }, [cars, searchTerm, selectedMake, selectedFuel, maxPrice, minYear, maxKm, selectedTransmission, sortBy])

  // Reset pagination count on filter changes
  useEffect(() => {
    setVisibleCount(12)
  }, [searchTerm, selectedMake, selectedFuel, maxPrice, minYear, maxKm, selectedTransmission, sortBy])

  // Infinite scroll observer
  useEffect(() => {
    if (visibleCount >= filteredCars.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + 12, filteredCars.length))
        }
      },
      { rootMargin: '300px' }
    )

    const currentSentinel = sentinelRef.current
    if (currentSentinel) observer.observe(currentSentinel)

    return () => {
      if (currentSentinel) observer.unobserve(currentSentinel)
    }
  }, [visibleCount, filteredCars.length])

  // Smart Dual-Directional Sticky Sidebar for Desktop
  useEffect(() => {
    const sidebar = sidebarRef.current
    const container = sidebarContainerRef.current
    if (!sidebar || !container) return

    const topSpacing = 144 // px below fixed navbar
    const bottomSpacing = 32 // px from bottom of viewport

    let lastScrollY = window.scrollY
    let ticking = false

    const updateSticky = () => {
      ticking = false

      // Only active on desktop (lg breakpoint >= 1024px)
      if (window.innerWidth < 1024) {
        sidebar.style.position = ''
        sidebar.style.top = ''
        sidebar.style.bottom = ''
        sidebar.style.width = ''
        return
      }

      const scrollY = window.scrollY
      const windowHeight = window.innerHeight
      const sidebarHeight = sidebar.offsetHeight
      const containerRect = container.getBoundingClientRect()
      const availableHeight = windowHeight - topSpacing - bottomSpacing

      const deltaY = scrollY - lastScrollY
      const isScrollingDown = deltaY > 0
      lastScrollY = scrollY

      // If sidebar fits completely inside viewport, standard sticky top
      if (sidebarHeight <= availableHeight) {
        sidebar.style.position = 'sticky'
        sidebar.style.top = `${topSpacing}px`
        sidebar.style.bottom = 'auto'
        sidebar.style.width = '100%'
        return
      }

      // Sidebar is taller than viewport: dual-directional scroll
      const sidebarRect = sidebar.getBoundingClientRect()
      const maxTop = Math.max(0, container.offsetHeight - sidebarHeight)

      if (isScrollingDown) {
        // Scrolling DOWN
        if (sidebarRect.bottom <= windowHeight - bottomSpacing) {
          // Bottom reached: stick to bottom
          sidebar.style.position = 'sticky'
          sidebar.style.top = `${windowHeight - bottomSpacing - sidebarHeight}px`
          sidebar.style.bottom = 'auto'
          sidebar.style.width = '100%'
        } else if (sidebar.style.position === 'sticky') {
          // Unstick from top: fix in place at current container offset
          const currentOffset = sidebarRect.top - containerRect.top
          sidebar.style.position = 'absolute'
          sidebar.style.top = `${Math.min(maxTop, Math.max(0, currentOffset))}px`
          sidebar.style.bottom = 'auto'
          sidebar.style.width = '100%'
        }
      } else {
        // Scrolling UP
        if (sidebarRect.top >= topSpacing) {
          // Top reached: stick to top
          sidebar.style.position = 'sticky'
          sidebar.style.top = `${topSpacing}px`
          sidebar.style.bottom = 'auto'
          sidebar.style.width = '100%'
        } else if (sidebar.style.position === 'sticky') {
          // Unstick from bottom: fix in place at current container offset
          const currentOffset = sidebarRect.top - containerRect.top
          sidebar.style.position = 'absolute'
          sidebar.style.top = `${Math.min(maxTop, Math.max(0, currentOffset))}px`
          sidebar.style.bottom = 'auto'
          sidebar.style.width = '100%'
        }
      }

      // If in absolute mode, ensure it never exceeds maxTop (e.g. if cars list changed)
      if (sidebar.style.position === 'absolute') {
        const topNum = parseFloat(sidebar.style.top) || 0
        if (topNum > maxTop) {
          sidebar.style.top = `${maxTop}px`
        }
      }

      // Fallback if no position is set yet on first render
      if (!sidebar.style.position) {
        sidebar.style.position = 'sticky'
        sidebar.style.top = `${topSpacing}px`
        sidebar.style.bottom = 'auto'
        sidebar.style.width = '100%'
      }
    }

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateSticky)
        ticking = true
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    const resizeObserver = new ResizeObserver(() => {
      onScroll()
    })
    resizeObserver.observe(sidebar)
    resizeObserver.observe(container)

    onScroll()

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      resizeObserver.disconnect()
    }
  }, [filteredCars.length, visibleCount, loading])

  // Slice cars for infinite scroll
  const visibleCars = useMemo(() => {
    return filteredCars.slice(0, visibleCount)
  }, [filteredCars, visibleCount])

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm('')
    setSelectedMake('all')
    setSelectedFuel('all')
    setMaxPrice('all')
    setMinYear('all')
    setMaxKm('all')
    setSelectedTransmission('all')
    setSortBy('newest')
    setMakeSearch('')
    setVisibleCount(12)
  }

  // Sidebar Filter Form Content (Reused between desktop sidebar & mobile modal)
  const renderFilterControls = (isMobile = false) => (
    <div className="space-y-6">
      {/* Brand / Make Filter */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <label className="text-xs font-bold uppercase tracking-wider text-neutral-800">
            Mærke
          </label>
          {selectedMake !== 'all' && (
            <button
              onClick={() => setSelectedMake('all')}
              className="text-[11px] font-semibold text-[#9b784a] hover:underline"
            >
              Alle mærker
            </button>
          )}
        </div>

        {/* Brand search if many makes */}
        {makesWithCount.length > 7 && (
          <div className="relative mb-2">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5 text-neutral-400">
              <i className="fa-regular fa-magnifying-glass text-[10px]" />
            </div>
            <input
              type="text"
              value={makeSearch}
              onChange={(e) => setMakeSearch(e.target.value)}
              placeholder="Find mærke..."
              className="w-full rounded-lg border border-stone-200 bg-stone-50 py-1.5 pl-7 pr-3 text-xs text-neutral-800 placeholder-neutral-400 focus:border-[#9b784a] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#9b784a]"
            />
          </div>
        )}

        {/* Brand list */}
        <div className="space-y-1 max-h-[185px] overflow-y-auto pr-1 scrollbar-thin">
          {/* Option: All brands */}
          {!makeSearch && (
            <button
              type="button"
              onClick={() => setSelectedMake('all')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                selectedMake === 'all'
                  ? 'bg-neutral-900 text-white shadow-sm'
                  : 'text-neutral-700 hover:bg-stone-100'
              }`}
            >
              <span>Alle mærker</span>
              <span
                className={`text-[11px] px-2 py-0.5 rounded-full ${
                  selectedMake === 'all'
                    ? 'bg-neutral-800 text-stone-200'
                    : 'bg-stone-200/70 text-neutral-600'
                }`}
              >
                {cars.length}
              </span>
            </button>
          )}

          {/* Individual Makes */}
          {filteredMakesList.map(({ name, count }) => {
              const isSelected = selectedMake === name
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setSelectedMake(isSelected ? 'all' : name)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    isSelected
                      ? 'bg-neutral-900 text-white shadow-sm'
                      : 'text-neutral-700 hover:bg-stone-100'
                  }`}
                >
                  <span className="truncate">{name}</span>
                  <span
                    className={`text-[11px] px-2 py-0.5 rounded-full ${
                      isSelected
                        ? 'bg-neutral-800 text-stone-200'
                        : 'bg-stone-200/70 text-neutral-600'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              )
            })}

          {filteredMakesList.length === 0 && (
            <p className="py-2 text-center text-xs text-neutral-400">Ingen mærker fundet</p>
          )}
        </div>
      </div>

      <div className="border-t border-stone-200" />

      {/* Max Price Filter */}
      <div>
        <label htmlFor={`price-${isMobile ? 'mobile' : 'desktop'}`} className="block text-xs font-bold uppercase tracking-wider text-neutral-800 mb-2">
          Maksimal pris
        </label>
        <div className="relative">
          <select
            id={`price-${isMobile ? 'mobile' : 'desktop'}`}
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            className="w-full appearance-none rounded-xl border border-stone-300 bg-white py-2.5 pl-3 pr-8 text-xs text-neutral-900 focus:border-[#9b784a] focus:outline-none focus:ring-1 focus:ring-[#9b784a]"
          >
            <option value="all">Alle priser</option>
            <option value="150000">Op til 150.000 kr.</option>
            <option value="200000">Op til 200.000 kr.</option>
            <option value="250000">Op til 250.000 kr.</option>
            <option value="300000">Op til 300.000 kr.</option>
            <option value="350000">Op til 350.000 kr.</option>
            <option value="400000">Op til 400.000 kr.</option>
            <option value="500000">Op til 500.000 kr.</option>
            <option value="750000">Op til 750.000 kr.</option>
            <option value="1000000">Op til 1.000.000 kr.</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400">
            <i className="fa-solid fa-chevron-down text-[10px]" />
          </div>
        </div>
      </div>

      {/* Min Model Year Filter */}
      <div>
        <label htmlFor={`year-${isMobile ? 'mobile' : 'desktop'}`} className="block text-xs font-bold uppercase tracking-wider text-neutral-800 mb-2">
          Minimum årgang
        </label>
        <div className="relative">
          <select
            id={`year-${isMobile ? 'mobile' : 'desktop'}`}
            value={minYear}
            onChange={(e) => setMinYear(e.target.value)}
            className="w-full appearance-none rounded-xl border border-stone-300 bg-white py-2.5 pl-3 pr-8 text-xs text-neutral-900 focus:border-[#9b784a] focus:outline-none focus:ring-1 focus:ring-[#9b784a]"
          >
            <option value="all">Alle årgange</option>
            <option value="2025">2025 eller nyere</option>
            <option value="2024">2024 eller nyere</option>
            <option value="2023">2023 eller nyere</option>
            <option value="2022">2022 eller nyere</option>
            <option value="2021">2021 eller nyere</option>
            <option value="2020">2020 eller nyere</option>
            <option value="2018">2018 eller nyere</option>
            <option value="2015">2015 eller nyere</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400">
            <i className="fa-solid fa-chevron-down text-[10px]" />
          </div>
        </div>
      </div>

      {/* Max Mileage Filter */}
      <div>
        <label htmlFor={`km-${isMobile ? 'mobile' : 'desktop'}`} className="block text-xs font-bold uppercase tracking-wider text-neutral-800 mb-2">
          Maksimalt kilometertal
        </label>
        <div className="relative">
          <select
            id={`km-${isMobile ? 'mobile' : 'desktop'}`}
            value={maxKm}
            onChange={(e) => setMaxKm(e.target.value)}
            className="w-full appearance-none rounded-xl border border-stone-300 bg-white py-2.5 pl-3 pr-8 text-xs text-neutral-900 focus:border-[#9b784a] focus:outline-none focus:ring-1 focus:ring-[#9b784a]"
          >
            <option value="all">Alle kilometertal</option>
            <option value="25000">Maks. 25.000 km</option>
            <option value="50000">Maks. 50.000 km</option>
            <option value="75000">Maks. 75.000 km</option>
            <option value="100000">Maks. 100.000 km</option>
            <option value="150000">Maks. 150.000 km</option>
            <option value="200000">Maks. 200.000 km</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400">
            <i className="fa-solid fa-chevron-down text-[10px]" />
          </div>
        </div>
      </div>

      {/* Transmission Filter */}
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-neutral-800 mb-2">
          Gearkasse
        </label>
        <div className="grid grid-cols-3 gap-1 rounded-xl bg-stone-100 p-1">
          <button
            type="button"
            onClick={() => setSelectedTransmission('all')}
            className={`py-1.5 text-xs font-semibold rounded-lg transition-all ${
              selectedTransmission === 'all'
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Alle
          </button>
          <button
            type="button"
            onClick={() => setSelectedTransmission('automat')}
            className={`py-1.5 text-xs font-semibold rounded-lg transition-all ${
              selectedTransmission === 'automat'
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Automat
          </button>
          <button
            type="button"
            onClick={() => setSelectedTransmission('manuel')}
            className={`py-1.5 text-xs font-semibold rounded-lg transition-all ${
              selectedTransmission === 'manuel'
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Manuel
          </button>
        </div>
      </div>

      {/* Reset Button (inside filter block) */}
      {activeFilterCount > 0 && (
        <button
          type="button"
          onClick={handleResetFilters}
          className="w-full py-2.5 px-4 rounded-xl border border-stone-300 text-xs font-semibold text-neutral-700 hover:bg-stone-100 hover:text-neutral-950 transition-colors flex items-center justify-center gap-2"
        >
          <i className="fa-solid fa-rotate-left text-[11px]" />
          Nulstil alle filtre ({activeFilterCount})
        </button>
      )}
    </div>
  )

  return (
    <>
      <Helmet>
        <title>Biler til salg | Recars ApS</title>
        <meta
          name="description"
          content="Køb eller sælg bil med tryghed og tillid. Hos Recars ApS gør vi bilhandlen nem og sikker for dig – uanset om du vil købe din næste bil eller sælge din nuværende."
        />
        <meta property="og:title" content="Biler til salg | Recars ApS" />
        <meta
          property="og:description"
          content="Køb eller sælg bil med tryghed og tillid. Hos Recars ApS gør vi bilhandlen nem og sikker for dig – uanset om du vil købe din næste bil eller sælge din nuværende."
        />
        <link rel="canonical" href="https://recars.dk/biler-til-salg/" />
      </Helmet>

      {/* Header Banner */}
      <section className="relative bg-[#F5F2EB] pt-32 sm:pt-40 lg:pt-44 pb-14 border-b border-stone-300">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-12 text-left">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9b784a]">
                Vores aktuelle udvalg i Børkop
              </span>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl font-display">
                Biler til salg
              </h1>
              <p className="mt-3 max-w-2xl text-sm text-neutral-600">
                Opdateres live fra Bilinfo. Alle biler er grundigt klargjort og klar til prøvekørsel og besigtigelse på pladsen på Industrivej 52 i Børkop.
              </p>
            </div>

            {/* Live status badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-stone-300/80 bg-white/80 backdrop-blur-sm px-3.5 py-1.5 shadow-sm self-start md:self-auto">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-xs font-medium text-neutral-700">
                {cars.length > 0 ? `${cars.length} biler klar til levering` : 'Henter biler...'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Inventory & Filter Section */}
      <section className="bg-[#FAF9F6] py-8 sm:py-12 min-h-[800px]">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-12">
          
          {/* Top Search & Quick Filters Card */}
          <div className="mb-8 rounded-2xl border border-stone-200/90 bg-white p-4 sm:p-5 shadow-sm space-y-4">
            {/* Top Row: Search Input + Sort Dropdown + Mobile Filter Toggle */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {/* Search Bar */}
              <div className="relative flex-1">
                <label htmlFor="main-car-search" className="sr-only">Søg bil eller udstyr</label>
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-neutral-400">
                  <i className="fa-regular fa-magnifying-glass text-sm" />
                </div>
                <input
                  id="main-car-search"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Søg model, variant eller udstyr (f.eks. adaptiv fartpilot, svingbart træk, varmepumpe)..."
                  className="w-full rounded-xl border border-stone-300 bg-stone-50/50 py-3 pl-10 pr-10 text-xs sm:text-sm text-neutral-900 placeholder-neutral-400 focus:border-[#9b784a] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#9b784a]"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    aria-label="Ryd søgefelt"
                    className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-neutral-400 hover:text-neutral-700"
                  >
                    <i className="fa-solid fa-xmark text-sm" />
                  </button>
                )}
              </div>

              {/* Action buttons: Mobile Filter Trigger + Sort */}
              <div className="flex items-center gap-2.5 sm:shrink-0">
                {/* Mobile Filter Toggle button (visible on < lg) */}
                <button
                  type="button"
                  onClick={() => setIsMobileFilterOpen(true)}
                  className="lg:hidden flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-xl border border-stone-300 bg-white py-3 px-4 text-xs font-semibold text-neutral-800 shadow-sm hover:bg-stone-50 transition-colors"
                >
                  <i className="fa-solid fa-sliders text-[#9b784a]" />
                  <span>Filtre</span>
                  {activeFilterCount > 0 && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-neutral-900 text-[10px] font-bold text-white">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {/* Sortering */}
                <div className="relative flex-1 sm:flex-none sm:min-w-[200px]">
                  <label htmlFor="top-sort" className="sr-only">Sorter biler</label>
                  <select
                    id="top-sort"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full appearance-none rounded-xl border border-stone-300 bg-white py-3 pl-3.5 pr-9 text-xs font-medium text-neutral-800 focus:border-[#9b784a] focus:outline-none focus:ring-1 focus:ring-[#9b784a]"
                  >
                    <option value="newest">Nyeste modelår først</option>
                    <option value="price-asc">Pris: Laveste først</option>
                    <option value="price-desc">Pris: Højeste først</option>
                    <option value="km-asc">Kilometertal: Laveste først</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400">
                    <i className="fa-solid fa-chevron-down text-[10px]" />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Row: Quick Drivetrain / Fuel Filter Pills */}
            <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-stone-100">
              <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mr-1 flex items-center gap-1.5">
                <i className="fa-solid fa-gas-pump text-[10px] text-[#9b784a]" /> Drivmiddel:
              </span>

              {/* All Fuels */}
              <button
                type="button"
                onClick={() => setSelectedFuel('all')}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all ${
                  selectedFuel === 'all'
                    ? 'bg-neutral-900 text-white shadow-sm ring-1 ring-neutral-900'
                    : 'bg-stone-100 text-neutral-700 hover:bg-stone-200'
                }`}
              >
                <span>Alle drivmidler</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                  selectedFuel === 'all' ? 'bg-neutral-800 text-stone-200' : 'bg-stone-200 text-neutral-600'
                }`}>
                  {fuelsWithCount.all}
                </span>
              </button>

              {/* 100% El */}
              {fuelsWithCount.el > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedFuel(selectedFuel === 'el' ? 'all' : 'el')}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all ${
                    selectedFuel === 'el'
                      ? 'bg-emerald-700 text-white shadow-sm ring-1 ring-emerald-700'
                      : 'bg-stone-100 text-neutral-700 hover:bg-stone-200'
                  }`}
                >
                  <i className="fa-solid fa-bolt text-emerald-500" />
                  <span>100% El</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                    selectedFuel === 'el' ? 'bg-emerald-800 text-white' : 'bg-stone-200 text-neutral-600'
                  }`}>
                    {fuelsWithCount.el}
                  </span>
                </button>
              )}

              {/* Benzin */}
              {fuelsWithCount.benzin > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedFuel(selectedFuel === 'benzin' ? 'all' : 'benzin')}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all ${
                    selectedFuel === 'benzin'
                      ? 'bg-neutral-900 text-white shadow-sm ring-1 ring-neutral-900'
                      : 'bg-stone-100 text-neutral-700 hover:bg-stone-200'
                  }`}
                >
                  <i className="fa-solid fa-gas-pump text-amber-500" />
                  <span>Benzin</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                    selectedFuel === 'benzin' ? 'bg-neutral-800 text-stone-200' : 'bg-stone-200 text-neutral-600'
                  }`}>
                    {fuelsWithCount.benzin}
                  </span>
                </button>
              )}

              {/* Diesel */}
              {fuelsWithCount.diesel > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedFuel(selectedFuel === 'diesel' ? 'all' : 'diesel')}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all ${
                    selectedFuel === 'diesel'
                      ? 'bg-neutral-900 text-white shadow-sm ring-1 ring-neutral-900'
                      : 'bg-stone-100 text-neutral-700 hover:bg-stone-200'
                  }`}
                >
                  <i className="fa-solid fa-droplet text-slate-500" />
                  <span>Diesel</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                    selectedFuel === 'diesel' ? 'bg-neutral-800 text-stone-200' : 'bg-stone-200 text-neutral-600'
                  }`}>
                    {fuelsWithCount.diesel}
                  </span>
                </button>
              )}

              {/* Hybrid */}
              {fuelsWithCount.hybrid > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedFuel(selectedFuel === 'hybrid' ? 'all' : 'hybrid')}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all ${
                    selectedFuel === 'hybrid'
                      ? 'bg-neutral-900 text-white shadow-sm ring-1 ring-neutral-900'
                      : 'bg-stone-100 text-neutral-700 hover:bg-stone-200'
                  }`}
                >
                  <i className="fa-solid fa-leaf text-teal-500" />
                  <span>Hybrid</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                    selectedFuel === 'hybrid' ? 'bg-neutral-800 text-stone-200' : 'bg-stone-200 text-neutral-600'
                  }`}>
                    {fuelsWithCount.hybrid}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Main Layout: 2 Columns on Desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Desktop Left Column: Smart Sticky Filter Sidebar */}
            <aside
              ref={sidebarContainerRef}
              className="hidden lg:block lg:col-span-3 self-stretch relative"
            >
              <div ref={sidebarRef} className="space-y-6 w-full lg:sticky lg:top-36">
                <div className="rounded-2xl border border-stone-200/90 bg-white p-5 shadow-sm">
                  {/* Sidebar Header */}
                  <div className="flex items-center justify-between pb-4 border-b border-stone-200">
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-stone-100 text-[#9b784a]">
                        <i className="fa-solid fa-sliders text-xs" />
                      </div>
                      <h2 className="font-display text-sm font-bold uppercase tracking-wider text-neutral-900">
                        Filtrering
                      </h2>
                    </div>
                    {activeFilterCount > 0 && (
                      <button
                        onClick={handleResetFilters}
                        className="text-xs font-semibold text-[#9b784a] hover:underline flex items-center gap-1"
                      >
                        <i className="fa-solid fa-rotate-left text-[10px]" /> Nulstil
                      </button>
                    )}
                  </div>

                  {/* Filter Form Controls */}
                  <div className="pt-4">
                    {renderFilterControls(false)}
                  </div>
                </div>

                {/* Sourcing / Lead-in Card */}
                <div className="rounded-2xl border border-[#DFB587]/50 bg-[#F5F2EB] p-5 text-left shadow-sm">
                  <div className="flex items-center gap-2 text-[#9b784a] mb-2">
                    <i className="fa-solid fa-car-side text-base" />
                    <h3 className="font-display text-sm font-bold text-neutral-900">
                      Søger du en specifik bil?
                    </h3>
                  </div>
                  <p className="text-xs text-neutral-600 leading-relaxed mb-4">
                    Vi skaffer også biler hjem på bestilling til vores plads i Børkop. Fortæl os om dine ønsker, så finder vi den rette bil til dig.
                  </p>
                  <div className="space-y-2">
                    <a
                      href="tel:+4571999177"
                      className="flex items-center gap-2 text-xs font-bold text-neutral-900 hover:text-[#9b784a] transition-colors"
                    >
                      <i className="fa-solid fa-phone text-[#9b784a] text-xs" /> +45 71 99 91 77
                    </a>
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center gap-1 text-xs font-bold text-[#9b784a] hover:underline"
                    >
                      Send bilønske til os <i className="fa-solid fa-arrow-right text-[10px]" />
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Right Column: Inventory Results & Grid */}
            <main className="lg:col-span-9">
              {/* Results Top Header & Active Filter Tags Bar */}
              <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-stone-200/80 pb-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-neutral-600">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#9b784a]" />
                  <span>
                    Viser <strong className="text-neutral-900 font-semibold">{filteredCars.length}</strong> {filteredCars.length === 1 ? 'bil' : 'biler'} til salg
                  </span>
                </div>

                {/* Reset all button if filters are active */}
                {activeFilterCount > 0 && (
                  <button
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#9b784a] hover:underline self-start sm:self-auto"
                  >
                    <i className="fa-solid fa-rotate-left text-[10px]" /> Nulstil alle filtre ({activeFilterCount})
                  </button>
                )}
              </div>

              {/* Active Filter Badges */}
              {activeFilterCount > 0 && (
                <div className="mb-6 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-neutral-500">Valgte filtre:</span>

                  {searchTerm.trim() && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-medium text-neutral-800 shadow-2xs">
                      Søgning: &ldquo;{searchTerm}&rdquo;
                      <button
                        onClick={() => setSearchTerm('')}
                        aria-label="Fjern søgefilter"
                        className="text-neutral-400 hover:text-neutral-900 ml-0.5"
                      >
                        <i className="fa-solid fa-xmark text-[10px]" />
                      </button>
                    </span>
                  )}

                  {selectedMake !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-medium text-neutral-800 shadow-2xs">
                      Mærke: {selectedMake}
                      <button
                        onClick={() => setSelectedMake('all')}
                        aria-label="Fjern mærkefilter"
                        className="text-neutral-400 hover:text-neutral-900 ml-0.5"
                      >
                        <i className="fa-solid fa-xmark text-[10px]" />
                      </button>
                    </span>
                  )}

                  {selectedFuel !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-medium text-neutral-800 shadow-2xs">
                      Drivmiddel: {selectedFuel === 'el' ? '100% El' : selectedFuel.charAt(0).toUpperCase() + selectedFuel.slice(1)}
                      <button
                        onClick={() => setSelectedFuel('all')}
                        aria-label="Fjern drivmiddelfilter"
                        className="text-neutral-400 hover:text-neutral-900 ml-0.5"
                      >
                        <i className="fa-solid fa-xmark text-[10px]" />
                      </button>
                    </span>
                  )}

                  {maxPrice !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-medium text-neutral-800 shadow-2xs">
                      Maks. {parseInt(maxPrice, 10).toLocaleString('da-DK')} kr.
                      <button
                        onClick={() => setMaxPrice('all')}
                        aria-label="Fjern prisfilter"
                        className="text-neutral-400 hover:text-neutral-900 ml-0.5"
                      >
                        <i className="fa-solid fa-xmark text-[10px]" />
                      </button>
                    </span>
                  )}

                  {minYear !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-medium text-neutral-800 shadow-2xs">
                      Årgang {minYear}+
                      <button
                        onClick={() => setMinYear('all')}
                        aria-label="Fjern årgangsfilter"
                        className="text-neutral-400 hover:text-neutral-900 ml-0.5"
                      >
                        <i className="fa-solid fa-xmark text-[10px]" />
                      </button>
                    </span>
                  )}

                  {maxKm !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-medium text-neutral-800 shadow-2xs">
                      Maks. {parseInt(maxKm, 10).toLocaleString('da-DK')} km
                      <button
                        onClick={() => setMaxKm('all')}
                        aria-label="Fjern kilometertalfilter"
                        className="text-neutral-400 hover:text-neutral-900 ml-0.5"
                      >
                        <i className="fa-solid fa-xmark text-[10px]" />
                      </button>
                    </span>
                  )}

                  {selectedTransmission !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-medium text-neutral-800 shadow-2xs">
                      Gear: {selectedTransmission === 'automat' ? 'Automatgear' : 'Manuel'}
                      <button
                        onClick={() => setSelectedTransmission('all')}
                        aria-label="Fjern gearkassefilter"
                        className="text-neutral-400 hover:text-neutral-900 ml-0.5"
                      >
                        <i className="fa-solid fa-xmark text-[10px]" />
                      </button>
                    </span>
                  )}
                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div className="py-24 text-center">
                  <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-[#9b784a] border-t-transparent" />
                  <p className="mt-4 text-sm text-neutral-600">Henter aktuelle biler fra Bilinfo...</p>
                </div>
              )}

              {/* Error State */}
              {!loading && error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
                  <i className="fa-regular fa-triangle-exclamation mb-3 text-3xl text-red-600" />
                  <p className="text-sm font-medium text-red-800">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-neutral-900 px-4 py-2 text-xs font-semibold text-white hover:bg-neutral-800"
                  >
                    Prøv igen
                  </button>
                </div>
              )}

              {/* Empty State */}
              {!loading && !error && filteredCars.length === 0 && (
                <div className="rounded-2xl border border-stone-200 bg-white p-12 sm:p-16 text-center shadow-sm">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-stone-100 text-[#9b784a]">
                    <i className="fa-solid fa-car text-2xl" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-neutral-900">Ingen biler matcher dine filtre</h3>
                  <p className="mt-2 text-xs sm:text-sm text-neutral-600 max-w-md mx-auto">
                    Vi fandt desværre ingen biler, der opfylder alle de valgte kriterier. Prøv at fjerne enkelte filtre eller nulstille for at se hele udvalget.
                  </p>
                  <button
                    onClick={handleResetFilters}
                    className="mt-6 inline-flex items-center gap-2 rounded-xl bg-neutral-950 px-5 py-2.5 text-xs font-bold text-white hover:bg-[#DFB587] hover:text-neutral-950 transition-colors"
                  >
                    <i className="fa-solid fa-rotate-left text-xs" /> Nulstil alle filtre
                  </button>
                </div>
              )}

              {/* Cars Grid */}
              {!loading && !error && visibleCars.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {visibleCars.map((car) => (
                    <CarCard key={car.Id || car.VehicleId || car.id} car={car} />
                  ))}
                </div>
              )}

              {/* Infinite Scroll Trigger / Sentinel */}
              {!loading && !error && visibleCount < filteredCars.length && (
                <div ref={sentinelRef} className="mt-12 flex flex-col items-center justify-center py-6">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-[#9b784a] border-t-transparent" />
                  <p className="mt-3 text-xs font-medium text-neutral-500">
                    Indlæser flere biler... ({visibleCars.length} af {filteredCars.length})
                  </p>
                </div>
              )}

              {/* End of list reached */}
              {!loading && !error && visibleCars.length > 0 && visibleCount >= filteredCars.length && filteredCars.length > 12 && (
                <div className="mt-14 border-t border-stone-200/80 pt-8 text-center">
                  <p className="text-xs text-neutral-500">
                    Du har set alle {filteredCars.length} biler på lager
                  </p>
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-4 py-2 text-xs font-semibold text-neutral-700 shadow-sm hover:border-stone-300 hover:bg-stone-50 transition-colors"
                  >
                    <i className="fa-solid fa-arrow-up text-[10px] text-[#9b784a]" /> Tilbage til toppen
                  </button>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      {/* Mobile Filter Slide-Over Drawer */}
      {isMobileFilterOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-neutral-950/60 backdrop-blur-xs transition-opacity"
            onClick={() => setIsMobileFilterOpen(false)}
          />

          {/* Drawer Panel */}
          <div className="relative ml-auto flex h-full w-full max-w-sm flex-col bg-white shadow-2xl overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-stone-200 p-4 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-sliders text-[#9b784a]" />
                <h2 className="font-display text-base font-bold text-neutral-900">
                  Filtrering ({filteredCars.length} biler)
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setIsMobileFilterOpen(false)}
                aria-label="Luk filtre"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 hover:bg-stone-100 hover:text-neutral-900"
              >
                <i className="fa-solid fa-xmark text-base" />
              </button>
            </div>

            {/* Filter Content */}
            <div className="flex-1 p-5">
              {renderFilterControls(true)}
            </div>

            {/* Sticky Bottom Action */}
            <div className="sticky bottom-0 border-t border-stone-200 bg-white p-4 shadow-lg flex items-center gap-3">
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="py-3 px-4 rounded-xl border border-stone-300 text-xs font-bold text-neutral-700 hover:bg-stone-50"
                >
                  Ryd
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsMobileFilterOpen(false)}
                className="flex-1 py-3 px-4 rounded-xl bg-neutral-900 text-xs font-bold text-white hover:bg-[#DFB587] hover:text-neutral-950 transition-colors text-center"
              >
                Vis {filteredCars.length} {filteredCars.length === 1 ? 'bil' : 'biler'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Maps Section */}
      <GoogleMap />
    </>
  )
}
